import React, { useEffect, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { GET_WATER_SYSTEM_RESULTS, GET_WATER_SYSTEM_SUMMARY } from "../../queries/publicQrys/SystemHeaderViewQrys";
import { ComboBox, ComboBoxOption } from "@utahdts/utah-design-system";
import { useDebounce } from "../../hooks/useDebounce";
import { useNavigateOnWaterSystemChange } from "../../hooks/useNavigateOnWaterSystemChange";
import { setPwsIdCookie } from "../../utilities/commonFunctions/pwsIdCookie";
import { useWaterSystemSearchModal } from "./WaterSystemSearchModalContext";
import "./WaterSystemSearchInput.css";

const WaterSystemSearchInput = ({ setShowSystemSelectModal, page, selectedWaterSystem, setSelectedWaterSystem }) => {

  const { setSelectedWaterSystem: setContextWaterSystem } = useWaterSystemSearchModal();
  const navigateOnWaterSystemChange = useNavigateOnWaterSystemChange();
  const abortControllerRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [searchWaterSystems, { data: searchData }] = useLazyQuery(GET_WATER_SYSTEM_RESULTS);
  const [getWaterSystemSummaryResult, { data: summaryData }] = useLazyQuery(GET_WATER_SYSTEM_SUMMARY);
  const [ waterSystems, setWaterSystems ] = React.useState([]);

  useEffect(() => {
    if (searchData?.waterSystemSearch) {
      setWaterSystems(searchData.waterSystemSearch);
    }
  }, [searchData]);

  const debouncedSearch = useDebounce((searchString) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (searchString && searchString.length > 0) {
      const controller = new AbortController();
      abortControllerRef.current = controller;

      searchWaterSystems({
        variables: { searchString },
        context: { signal: controller.signal },
      }).catch(error => {
        if (error.name !== 'AbortError') {
          console.error("Search query error:", error);
        }
      });
    } else {
      setWaterSystems([]); // Clear results if search input is empty
    }
  }, 500);

  const handleKeyUp = (e) => {
    const currentFilterValue = e.target.value;
    setSearchTerm(currentFilterValue);
    debouncedSearch(currentFilterValue);
    return true; // Prevent default ComboBox filtering
  };

  const handleSelect = (pwsId) => {
    // This function now only handles selection from the dropdown
    if (pwsId) {
      getWaterSystemSummaryResult({ variables: { pwsId } });
    }
  };

  useEffect(() => {
    // query the tooltip for the combobox button and change the text
    const wrapper = document.querySelector('.text-input__inner-wrapper');
    if (wrapper) {
      const tooltipContent = wrapper.querySelector('.tooltip__content');
      if (tooltipContent) {
        tooltipContent.innerHTML = "Toggle Search List";
      }
    }
  }, [waterSystems, page]);

  // Handle summary data after query completes
  useEffect(() => {
    if (summaryData && summaryData.getWaterSystemSummary) {
      saveSelectedWaterSystem(summaryData.getWaterSystemSummary);
      if (typeof setShowSystemSelectModal === "function") {
        setShowSystemSelectModal(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryData]);

  const saveSelectedWaterSystem = (waterSystemSummary) => {
    sessionStorage.setItem("selectedWaterSystem", JSON.stringify(waterSystemSummary)
    );
    setPwsIdCookie(waterSystemSummary.pwsId);
    setContextWaterSystem(waterSystemSummary);
    if (typeof setSelectedWaterSystem === "function") {
      setSelectedWaterSystem(waterSystemSummary);
    }

    navigateOnWaterSystemChange(window.location.pathname);
  };

  return (
    <div className="utah-design-system">
      <ComboBox
        className={page === "home"? "ws-search-input-homepage": "ws-search-input-modal"}
        id={page === "home"? "wsSearch-homepage": "wsSearch-modal"}
        name="wsSearch"
        autoComplete="off"
        label="Search for a Water System / by County / PWS ID / Report"
        labelClassName="visually-hidden"
        placeholder="Search for a Water System / by County / PWS ID / Report"
        aria-label="Search for a Water System / by County / PWS ID / Report"
        firstSelectableByEnter={true}
        value={searchTerm}
        onChange={handleSelect}
        onInput={handleKeyUp}
      >
        {waterSystems.map((result) => (
          <ComboBoxOption
            className={page === "home"? "combo-box-option-homepage": "combo-box-option-modal"}
            key={result.pwsId}
            label={result.county + " / " + result.systemName + " / " + result.alternateNum + " / " + result.systemType}
            value={result.pwsId}
          />
        ))}
      </ComboBox>
    </div>
  );
}

export default React.memo(WaterSystemSearchInput);